/**
 * Hala Learning Center Page
 * View, manage, and apply AI learning feedbacks
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Brain, Search, Filter, Check, XCircle, AlertCircle, 
  Eye, Edit2, Trash2, CheckCircle, Clock, Zap,
  Target, BookOpen, TrendingUp, RefreshCw, ChevronDown,
  MessageSquare, User, Bot, ThumbsUp, ThumbsDown, Minus, ArrowRight, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const FEEDBACK_TYPES = {
  correct: { label: 'صحيحة', color: COLORS.success, icon: Check, bg: COLORS.successLight },
  partial: { label: 'مقبولة', color: COLORS.warning, icon: AlertCircle, bg: COLORS.warningLight },
  wrong: { label: 'خاطئة', color: COLORS.error, icon: XCircle, bg: COLORS.errorLight },
};

const STATUS_CONFIG = {
  pending: { label: 'بانتظار المراجعة', color: COLORS.warning, icon: Clock },
  approved: { label: 'تم الاعتماد', color: COLORS.info, icon: CheckCircle },
  applied: { label: 'تم التطبيق', color: COLORS.success, icon: Zap },
  rejected: { label: 'مرفوض', color: COLORS.error, icon: XCircle },
};

const ERROR_REASON_LABELS = {
  intent_wrong: 'Intent خطأ',
  need_wrong: 'Need خطأ',
  service_wrong: 'Service خطأ',
  question_inappropriate: 'سؤال غير مناسب',
  missing_context: 'سياق مفقود',
};

export default function LearningCenterPage() {
  const [activeTab, setActiveTab] = useState('review'); // 'review' | 'feedbacks'
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    feedback_type: '',
    search: '',
  });
  const [page, setPage] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const limit = 20;

  // Session Review state
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionPage, setSessionPage] = useState(0);
  const [reviewFilter, setReviewFilter] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(null); // message_id being evaluated
  const [evalForm, setEvalForm] = useState({ feedback_type: '', notes: '', error_reasons: [] });

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, [filters, page]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      let url = `${API}/learning/feedback?skip=${page * limit}&limit=${limit}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.feedback_type) url += `&feedback_type=${filters.feedback_type}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setFeedbacks(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch feedbacks:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/learning/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  // ── Session Review Functions ──
  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      let url = `${API}/learning/review/sessions?skip=${sessionPage * limit}&limit=${limit}`;
      if (reviewFilter) url += `&status=${reviewFilter}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setSessions(res.data.items || []);
      setSessionsTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch sessions:', e);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchSessionDetail = async (sessionId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/learning/review/sessions/${sessionId}`, { headers: getAuthHeaders() });
      setSessionDetail(res.data);
    } catch (e) {
      console.error('Failed to fetch session detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitEvaluation = async (messageId) => {
    if (!evalForm.feedback_type) return;
    setActionLoading(messageId);
    try {
      await axios.post(`${API}/learning/review/evaluate`, {
        session_id: sessionDetail.session_id,
        message_id: messageId,
        feedback_type: evalForm.feedback_type,
        admin_notes: evalForm.notes,
        error_reasons: evalForm.error_reasons,
      }, { headers: getAuthHeaders() });
      // Refresh session detail to show updated evaluations
      await fetchSessionDetail(sessionDetail.session_id);
      setEvaluating(null);
      setEvalForm({ feedback_type: '', notes: '', error_reasons: [] });
      await fetchStats();
    } catch (e) {
      console.error('Failed to submit evaluation:', e);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'review') {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sessionPage, reviewFilter]);

  const handleApprove = async (feedbackId) => {
    setActionLoading(feedbackId);
    try {
      await axios.post(`${API}/learning/feedback/${feedbackId}/approve`, {}, { headers: getAuthHeaders() });
      await fetchFeedbacks();
      await fetchStats();
    } catch (e) {
      console.error('Failed to approve:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApply = async (feedbackId) => {
    setActionLoading(feedbackId);
    try {
      await axios.post(`${API}/learning/feedback/${feedbackId}/apply`, {}, { headers: getAuthHeaders() });
      await fetchFeedbacks();
      await fetchStats();
    } catch (e) {
      console.error('Failed to apply:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التقييم؟')) return;
    setActionLoading(feedbackId);
    try {
      await axios.delete(`${API}/learning/feedback/${feedbackId}`, { headers: getAuthHeaders() });
      await fetchFeedbacks();
      await fetchStats();
    } catch (e) {
      console.error('Failed to delete:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${COLORS.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Brain size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: 0 }}>
              مركز تعلم هلا
            </h1>
            <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: '4px 0 0' }}>
              إدارة واعتماد بيانات التعلم لتحسين النظام
            </p>
          </div>
        </div>
      </div>

      {/* Introduction Banner */}
      <div style={{
        backgroundColor: `${COLORS.info}08`,
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        borderRight: `4px solid ${COLORS.info}`,
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600, color: COLORS.info }}>
          ما هي هذه الصفحة؟
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.7 }}>
          هنا تجد جميع التقييمات التي أضافها الفريق على ردود هلا. يمكنك <strong>مراجعة</strong> كل تقييم، 
          <strong> اعتماده</strong> كبيانات تدريب صحيحة، ثم <strong>تطبيقه</strong> ليصبح قاعدة تعلم 
          تحسّن توصيات هلا المستقبلية. كل تصحيح تعتمده يجعل هلا أذكى!
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '24px',
        borderBottom: `2px solid ${COLORS.border}`,
      }}>
        {[
          { id: 'review', label: 'مراجعة الجلسات', icon: MessageSquare, count: sessionsTotal },
          { id: 'feedbacks', label: 'التقييمات', icon: Target, count: total },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '14px 24px',
              border: 'none',
              backgroundColor: 'transparent',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? COLORS.primary : COLORS.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: activeTab === tab.id ? `3px solid ${COLORS.primary}` : '3px solid transparent',
              marginBottom: '-2px',
              fontFamily: 'inherit',
            }}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: activeTab === tab.id ? `${COLORS.primary}15` : COLORS.bg,
                fontSize: '12px',
                fontWeight: 600,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── SESSION REVIEW TAB ── */}
      {activeTab === 'review' && (
        <div>
          {/* Review Filters */}
          <div style={{
            display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center',
          }}>
            <select
              data-testid="review-status-filter"
              value={reviewFilter}
              onChange={e => { setReviewFilter(e.target.value); setSessionPage(0); }}
              style={{
                padding: '10px 14px', borderRadius: '8px',
                border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white,
                fontSize: '13px', fontFamily: 'inherit', minWidth: '160px'
              }}
            >
              <option value="">كل الجلسات</option>
              <option value="pending">بانتظار المراجعة</option>
              <option value="reviewed">تمت مراجعتها</option>
            </select>
            <button onClick={fetchSessions} style={{
              padding: '10px 16px', borderRadius: '8px',
              border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white,
              fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <RefreshCw size={14} /> تحديث
            </button>
          </div>

          {/* Session Detail Modal */}
          {sessionDetail && (
            <>
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}
                onClick={() => { setSessionDetail(null); setEvaluating(null); }} />
              <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '800px', maxWidth: '95vw', maxHeight: '85vh',
                backgroundColor: COLORS.white, borderRadius: '16px',
                boxShadow: '0 25px 50px rgba(0,0,0,0.25)', zIndex: 1001,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}>
                {/* Modal Header */}
                <div style={{
                  padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>
                      مراجعة المحادثة
                    </h3>
                    <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>
                      {sessionDetail.city && `${sessionDetail.city} • `}
                      {sessionDetail.support_type && `${sessionDetail.support_type} • `}
                      {sessionDetail.state}
                    </p>
                  </div>
                  <button onClick={() => { setSessionDetail(null); setEvaluating(null); }} style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: COLORS.textMuted
                  }}>x</button>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                  {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textMuted }}>جاري التحميل...</div>
                  ) : (
                    (sessionDetail.messages || []).map((msg, idx) => {
                      const isAI = msg.sender === 'assistant';
                      const hasEval = !!msg.evaluation;
                      return (
                        <div key={msg.message_id || idx} style={{ marginBottom: '16px' }}>
                          {/* Message Bubble */}
                          <div style={{
                            display: 'flex', flexDirection: isAI ? 'row' : 'row-reverse',
                            alignItems: 'flex-start', gap: '10px',
                          }}>
                            <div style={{
                              width: '32px', height: '32px', borderRadius: '50%',
                              backgroundColor: isAI ? `${COLORS.primary}15` : `${COLORS.info}15`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              {isAI ? <Bot size={16} color={COLORS.primary} /> : <User size={16} color={COLORS.info} />}
                            </div>
                            <div style={{
                              maxWidth: '85%',
                              backgroundColor: isAI ? `${COLORS.primary}08` : COLORS.bg,
                              padding: '12px 16px', borderRadius: '12px',
                              borderTopRightRadius: isAI ? '4px' : '12px',
                              borderTopLeftRadius: isAI ? '12px' : '4px',
                              border: isAI ? `1px solid ${COLORS.primary}20` : `1px solid ${COLORS.border}`,
                            }}>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.7, color: COLORS.text, whiteSpace: 'pre-wrap' }}>
                                {msg.content}
                              </p>
                            </div>
                          </div>

                          {/* Evaluation Area for AI messages */}
                          {isAI && (
                            <div style={{ marginRight: '42px', marginTop: '8px' }}>
                              {hasEval ? (
                                <div style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                                  padding: '6px 12px', borderRadius: '6px',
                                  backgroundColor: msg.evaluation.feedback_type === 'correct' ? COLORS.successLight :
                                    msg.evaluation.feedback_type === 'partial' ? `${COLORS.warning}15` : COLORS.errorLight,
                                }}>
                                  {msg.evaluation.feedback_type === 'correct' ? <ThumbsUp size={14} color={COLORS.success} /> :
                                   msg.evaluation.feedback_type === 'partial' ? <Minus size={14} color={COLORS.warning} /> :
                                   <ThumbsDown size={14} color={COLORS.error} />}
                                  <span style={{
                                    fontSize: '12px', fontWeight: 500,
                                    color: msg.evaluation.feedback_type === 'correct' ? COLORS.success :
                                      msg.evaluation.feedback_type === 'partial' ? COLORS.warning : COLORS.error
                                  }}>
                                    {msg.evaluation.feedback_type === 'correct' ? 'صحيح' :
                                     msg.evaluation.feedback_type === 'partial' ? 'جزئي' : 'خطأ'}
                                  </span>
                                  {msg.evaluation.admin_notes && (
                                    <span style={{ fontSize: '11px', color: COLORS.textMuted }}>- {msg.evaluation.admin_notes}</span>
                                  )}
                                </div>
                              ) : evaluating === (msg.message_id || idx) ? (
                                /* Evaluation Form */
                                <div style={{
                                  padding: '14px', borderRadius: '10px',
                                  backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`,
                                }}>
                                  <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: COLORS.text }}>تقييم هذا الرد:</p>
                                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                    {[
                                      { type: 'correct', label: 'صحيح', icon: ThumbsUp, color: COLORS.success },
                                      { type: 'partial', label: 'جزئي', icon: Minus, color: COLORS.warning },
                                      { type: 'wrong', label: 'خطأ', icon: ThumbsDown, color: COLORS.error },
                                    ].map(opt => (
                                      <button key={opt.type} onClick={() => setEvalForm(f => ({ ...f, feedback_type: opt.type }))}
                                        style={{
                                          padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                                          border: evalForm.feedback_type === opt.type ? `2px solid ${opt.color}` : `1px solid ${COLORS.border}`,
                                          backgroundColor: evalForm.feedback_type === opt.type ? `${opt.color}15` : COLORS.white,
                                          display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontFamily: 'inherit',
                                          color: evalForm.feedback_type === opt.type ? opt.color : COLORS.textMuted,
                                        }}>
                                        <opt.icon size={14} /> {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                  {evalForm.feedback_type === 'wrong' && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <p style={{ margin: '0 0 6px', fontSize: '12px', color: COLORS.textMuted }}>سبب الخطأ:</p>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {Object.entries(ERROR_REASON_LABELS).map(([key, label]) => (
                                          <button key={key} onClick={() => setEvalForm(f => ({
                                            ...f, error_reasons: f.error_reasons.includes(key) ? f.error_reasons.filter(r => r !== key) : [...f.error_reasons, key]
                                          }))} style={{
                                            padding: '4px 10px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit',
                                            border: evalForm.error_reasons.includes(key) ? `1px solid ${COLORS.error}` : `1px solid ${COLORS.border}`,
                                            backgroundColor: evalForm.error_reasons.includes(key) ? COLORS.errorLight : COLORS.white,
                                            color: evalForm.error_reasons.includes(key) ? COLORS.error : COLORS.textMuted,
                                          }}>{label}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <input type="text" placeholder="ملاحظات (اختياري)..." value={evalForm.notes}
                                    onChange={e => setEvalForm(f => ({ ...f, notes: e.target.value }))}
                                    style={{
                                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                                      border: `1px solid ${COLORS.border}`, fontSize: '13px',
                                      fontFamily: 'inherit', marginBottom: '12px', boxSizing: 'border-box',
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                    <button onClick={() => { setEvaluating(null); setEvalForm({ feedback_type: '', notes: '', error_reasons: [] }); }}
                                      style={{
                                        padding: '8px 16px', borderRadius: '6px', border: `1px solid ${COLORS.border}`,
                                        backgroundColor: COLORS.white, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
                                      }}>إلغاء</button>
                                    <button onClick={() => submitEvaluation(msg.message_id || `msg_${idx}`)}
                                      disabled={!evalForm.feedback_type || actionLoading === (msg.message_id || idx)}
                                      style={{
                                        padding: '8px 16px', borderRadius: '6px', border: 'none',
                                        backgroundColor: evalForm.feedback_type ? COLORS.primary : COLORS.border,
                                        color: COLORS.white, fontSize: '13px', cursor: evalForm.feedback_type ? 'pointer' : 'not-allowed',
                                        fontFamily: 'inherit',
                                      }}>حفظ التقييم</button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  data-testid={`evaluate-btn-${idx}`}
                                  onClick={() => { setEvaluating(msg.message_id || idx); setEvalForm({ feedback_type: '', notes: '', error_reasons: [] }); }}
                                  style={{
                                    padding: '4px 12px', borderRadius: '6px',
                                    border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white,
                                    fontSize: '12px', cursor: 'pointer', color: COLORS.textMuted,
                                    display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit',
                                  }}>
                                  <Edit2 size={12} /> تقييم
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}

          {/* Sessions List */}
          <div style={{
            backgroundColor: COLORS.white, borderRadius: '16px',
            border: `1px solid ${COLORS.border}`, overflow: 'hidden',
          }}>
            {sessionsLoading ? (
              <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>جاري التحميل...</div>
            ) : sessions.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
                <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: '16px' }}>لا توجد جلسات</p>
              </div>
            ) : (
              <div>
                {sessions.map(session => (
                  <div key={session.session_id}
                    data-testid={`session-row-${session.session_id}`}
                    onClick={() => { setSelectedSession(session.session_id); fetchSessionDetail(session.session_id); }}
                    style={{
                      padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}`,
                      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.bg}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                          backgroundColor: session.is_reviewed ? COLORS.successLight : `${COLORS.warning}15`,
                          color: session.is_reviewed ? COLORS.success : COLORS.warning,
                        }}>
                          {session.is_reviewed ? 'تمت المراجعة' : 'بانتظار المراجعة'}
                        </span>
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px',
                          backgroundColor: COLORS.bg, color: COLORS.textMuted,
                        }}>
                          {session.state}
                        </span>
                        {session.city && (
                          <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{session.city}</span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: COLORS.text, lineHeight: 1.5 }}>
                        {session.last_ai_message || 'لا توجد رسائل'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'left', flexShrink: 0, marginRight: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <MessageSquare size={12} color={COLORS.textMuted} />
                        <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{session.message_count} رسالة</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FileText size={12} color={COLORS.textMuted} />
                        <span style={{ fontSize: '12px', color: COLORS.textMuted }}>{session.feedback_count} تقييم</span>
                      </div>
                    </div>
                    <ArrowRight size={16} color={COLORS.textLight} />
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {sessionsTotal > limit && (
              <div style={{
                padding: '16px 20px', borderTop: `1px solid ${COLORS.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                  عرض {sessionPage * limit + 1} - {Math.min((sessionPage + 1) * limit, sessionsTotal)} من {sessionsTotal}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setSessionPage(p => Math.max(0, p - 1))} disabled={sessionPage === 0}
                    style={{ padding: '8px 14px', borderRadius: '6px', border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white, fontSize: '13px', cursor: sessionPage === 0 ? 'not-allowed' : 'pointer', opacity: sessionPage === 0 ? 0.5 : 1 }}>
                    السابق
                  </button>
                  <button onClick={() => setSessionPage(p => p + 1)} disabled={(sessionPage + 1) * limit >= sessionsTotal}
                    style={{ padding: '8px 14px', borderRadius: '6px', border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white, fontSize: '13px', cursor: (sessionPage + 1) * limit >= sessionsTotal ? 'not-allowed' : 'pointer', opacity: (sessionPage + 1) * limit >= sessionsTotal ? 0.5 : 1 }}>
                    التالي
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FEEDBACKS TAB ── */}
      {activeTab === 'feedbacks' && (
      <>
      {/* Stats Cards */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Accuracy Rate */}
          <div style={{
            backgroundColor: COLORS.white,
            padding: '20px',
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <TrendingUp size={18} color={COLORS.success} />
              <span style={{ fontSize: '13px', color: COLORS.textMuted }}>دقة النظام</span>
            </div>
            <p style={{ 
              fontSize: '32px', 
              fontWeight: 700, 
              color: stats.accuracy_rate >= 80 ? COLORS.success : stats.accuracy_rate >= 50 ? COLORS.warning : COLORS.error,
              margin: 0 
            }}>
              {stats.accuracy_rate}%
            </p>
          </div>

          {/* Total Feedbacks */}
          <div style={{
            backgroundColor: COLORS.white,
            padding: '20px',
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Target size={18} color={COLORS.primary} />
              <span style={{ fontSize: '13px', color: COLORS.textMuted }}>إجمالي التقييمات</span>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 700, color: COLORS.primary, margin: 0 }}>
              {stats.total_feedbacks}
            </p>
          </div>

          {/* Pending */}
          <div style={{
            backgroundColor: COLORS.warningLight,
            padding: '20px',
            borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Clock size={18} color={COLORS.warning} />
              <span style={{ fontSize: '13px', color: COLORS.warning }}>بانتظار المراجعة</span>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 700, color: COLORS.warning, margin: 0 }}>
              {stats.by_status?.pending || 0}
            </p>
          </div>

          {/* Applied */}
          <div style={{
            backgroundColor: COLORS.successLight,
            padding: '20px',
            borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Zap size={18} color={COLORS.success} />
              <span style={{ fontSize: '13px', color: COLORS.success }}>تم تطبيقه</span>
            </div>
            <p style={{ fontSize: '32px', fontWeight: 700, color: COLORS.success, margin: 0 }}>
              {stats.by_status?.applied || 0}
            </p>
          </div>
        </div>
      )}

      {/* Top Errors */}
      {stats?.top_errors && stats.top_errors.length > 0 && (
        <div style={{
          backgroundColor: COLORS.errorLight,
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          borderRight: `4px solid ${COLORS.error}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <AlertCircle size={18} color={COLORS.error} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.error }}>
              أكثر الأخطاء شيوعاً
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {stats.top_errors.map((err, idx) => (
              <span key={idx} style={{
                padding: '6px 12px',
                borderRadius: '6px',
                backgroundColor: COLORS.white,
                fontSize: '13px',
                color: COLORS.error,
                fontWeight: 500,
              }}>
                {ERROR_REASON_LABELS[err.reason] || err.reason} ({err.count})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.bg,
          padding: '10px 14px',
          borderRadius: '8px',
          flex: 1,
          minWidth: '250px'
        }}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            placeholder="بحث في النص أو الملاحظات..."
            value={filters.search}
            onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>

        <select
          value={filters.status}
          onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }}
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            fontFamily: 'inherit',
            minWidth: '160px'
          }}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <select
          value={filters.feedback_type}
          onChange={e => { setFilters(f => ({ ...f, feedback_type: e.target.value })); setPage(0); }}
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            fontFamily: 'inherit',
            minWidth: '140px'
          }}
        >
          <option value="">كل التقييمات</option>
          {Object.entries(FEEDBACK_TYPES).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <button
          onClick={() => { fetchFeedbacks(); fetchStats(); }}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <RefreshCw size={14} />
          تحديث
        </button>
      </div>

      {/* Feedbacks List */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : feedbacks.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            <Brain size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ margin: 0, fontSize: '16px' }}>لا توجد تقييمات</p>
            <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.7 }}>
              ابدأ بتقييم رسائل هلا من صفحة الجلسات
            </p>
          </div>
        ) : (
          <div>
            {feedbacks.map(feedback => {
              const feedbackType = FEEDBACK_TYPES[feedback.feedback_type] || FEEDBACK_TYPES.correct;
              const status = STATUS_CONFIG[feedback.status] || STATUS_CONFIG.pending;
              const FeedbackIcon = feedbackType.icon;
              const StatusIcon = status.icon;
              
              return (
                <div 
                  key={feedback.feedback_id}
                  style={{
                    padding: '20px',
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  {/* Header Row */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {/* Feedback Type Badge */}
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        backgroundColor: feedbackType.bg,
                        color: feedbackType.color,
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        <FeedbackIcon size={14} />
                        {feedbackType.label}
                      </span>
                      
                      {/* Status Badge */}
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        backgroundColor: `${status.color}15`,
                        color: status.color,
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        <StatusIcon size={14} />
                        {status.label}
                      </span>
                    </div>
                    
                    <span style={{ fontSize: '12px', color: COLORS.textLight }}>
                      {formatDate(feedback.created_at)}
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div style={{ marginBottom: '16px' }}>
                    {/* User Input */}
                    {feedback.user_input && (
                      <div style={{ marginBottom: '10px' }}>
                        <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
                          سؤال المستخدم:
                        </p>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '13px', 
                          color: COLORS.text,
                          backgroundColor: COLORS.bg,
                          padding: '10px 12px',
                          borderRadius: '8px',
                        }}>
                          {feedback.user_input}
                        </p>
                      </div>
                    )}
                    
                    {/* AI Response */}
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
                        رد هلا:
                      </p>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '13px', 
                        color: COLORS.text,
                        backgroundColor: `${COLORS.primary}08`,
                        padding: '10px 12px',
                        borderRadius: '8px',
                        borderRight: `3px solid ${COLORS.primary}`,
                      }}>
                        {feedback.ai_response?.substring(0, 200)}
                        {feedback.ai_response?.length > 200 && '...'}
                      </p>
                    </div>
                    
                    {/* Error Reasons */}
                    {feedback.error_reasons && feedback.error_reasons.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>
                          أسباب الخطأ:
                        </p>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {feedback.error_reasons.map(reason => (
                            <span key={reason} style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: COLORS.errorLight,
                              color: COLORS.error,
                              fontSize: '11px',
                            }}>
                              {ERROR_REASON_LABELS[reason] || reason}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Corrections */}
                    {(feedback.correct_intent || feedback.correct_need || feedback.correct_service) && (
                      <div style={{
                        backgroundColor: COLORS.successLight,
                        padding: '12px',
                        borderRadius: '8px',
                        marginTop: '10px',
                      }}>
                        <p style={{ fontSize: '11px', color: COLORS.success, margin: '0 0 8px', fontWeight: 600 }}>
                          التصحيحات:
                        </p>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          {feedback.correct_intent && (
                            <span style={{ fontSize: '12px', color: COLORS.text }}>
                              <strong>Intent:</strong> {feedback.correct_intent}
                            </span>
                          )}
                          {feedback.correct_need && (
                            <span style={{ fontSize: '12px', color: COLORS.text }}>
                              <strong>Need:</strong> {feedback.correct_need}
                            </span>
                          )}
                          {feedback.correct_service && (
                            <span style={{ fontSize: '12px', color: COLORS.text }}>
                              <strong>Service:</strong> {feedback.correct_service}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Notes */}
                    {feedback.notes && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
                          ملاحظات:
                        </p>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '12px', 
                          color: COLORS.textMuted,
                          fontStyle: 'italic',
                        }}>
                          {feedback.notes}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    {feedback.status === 'pending' && (
                      <button
                        onClick={() => handleApprove(feedback.feedback_id)}
                        disabled={actionLoading === feedback.feedback_id}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: COLORS.info,
                          color: COLORS.white,
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: actionLoading === feedback.feedback_id ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          opacity: actionLoading === feedback.feedback_id ? 0.7 : 1,
                        }}
                      >
                        <CheckCircle size={14} />
                        اعتماد
                      </button>
                    )}
                    
                    {feedback.status === 'approved' && (
                      <button
                        onClick={() => handleApply(feedback.feedback_id)}
                        disabled={actionLoading === feedback.feedback_id}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: COLORS.success,
                          color: COLORS.white,
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: actionLoading === feedback.feedback_id ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          opacity: actionLoading === feedback.feedback_id ? 0.7 : 1,
                        }}
                      >
                        <Zap size={14} />
                        تطبيق كقاعدة تعلم
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDelete(feedback.feedback_id)}
                      disabled={actionLoading === feedback.feedback_id}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.white,
                        color: COLORS.textMuted,
                        fontSize: '13px',
                        cursor: actionLoading === feedback.feedback_id ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div style={{ 
            padding: '16px 20px', 
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
              عرض {page * limit + 1} - {Math.min((page + 1) * limit, total)} من {total}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '13px',
                  cursor: page === 0 ? 'not-allowed' : 'pointer',
                  opacity: page === 0 ? 0.5 : 1
                }}
              >
                السابق
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '13px',
                  cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer',
                  opacity: (page + 1) * limit >= total ? 0.5 : 1
                }}
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
